/** Calendar date at midnight, built from year/month/day parts only. */
function dayAtMidnight(year: number, monthIndex: number, day: number): Date {
  const cursor = new Date(0);
  cursor.setUTCFullYear(year, monthIndex, day);
  cursor.setUTCHours(0, 0, 0, 0);
  return cursor;
}

/** Every calendar day of 2026 — the Excel year grid, not one day. */
export function daysOf2026(): string[] {
  const out: string[] = [];
  const cursor = dayAtMidnight(2026, 0, 1);
  const last = dayAtMidnight(2026, 11, 31).getTime();
  while (cursor.getTime() <= last) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/** YYYY-MM-DD from whatever the API stored — never through Date (F-62). */
export function asReconDay(raw: string | null | undefined): string {
  const s = String(raw ?? '');
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1] : s;
}

/** MM/DD/YYYY from a YYYY-MM-DD string — never through Date (F-62). */
export function formatReconDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(asReconDay(iso));
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

export function parseMoneyCell(raw: string): number | null {
  const t = String(raw ?? '').trim().replace(/[$,]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Envelope minus MR080 when both cells are numbers — the Excel Over/Under. */
export function dailyOverUnder(mr080: string, envelope: string): string {
  const a = parseMoneyCell(mr080);
  const b = parseMoneyCell(envelope);
  if (a === null || b === null) return '';
  return (b - a).toFixed(2);
}

/** Excel: actual deposit minus MR080 (2026 sheet C−D, 2025 E−F). */
export function batchOverUnder(batchAmount: string, mr080Batch: string): string {
  const a = parseMoneyCell(batchAmount);
  const b = parseMoneyCell(mr080Batch);
  if (a === null || b === null) return '';
  return (a - b).toFixed(2);
}

export function selectedRange(days: string[]): { start: string; end: string } | null {
  if (!days.length) return null;
  const sorted = [...days].sort();
  return { start: sorted[0], end: sorted[sorted.length - 1] };
}

export function dayInRange(day: string, start: string, end: string): boolean {
  return day >= start && day <= end;
}

/** The calendar day after YYYY-MM-DD — date parts only (F-62). */
export function nextReconDay(iso: string): string {
  const day = asReconDay(iso);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const cursor = dayAtMidnight(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(cursor.getTime())) return day;
  cursor.setUTCDate(cursor.getUTCDate() + 1);
  return cursor.toISOString().slice(0, 10);
}

/** A closed batch counts as paid when MR080 or the batch amount is not zero. */
export function batchHasMoney(
  mr080Amount: string | number | null | undefined,
  batchAmount: string | number | null | undefined,
): boolean {
  const a = parseMoneyCell(mr080Amount == null ? '' : String(mr080Amount));
  const b = parseMoneyCell(batchAmount == null ? '' : String(batchAmount));
  return (a != null && a !== 0) || (b != null && b !== 0);
}

/** Latest dateEnd among batches that actually have money — skip $0 Excel tails. */
export function lastPaidBatchEnd(
  batches: Array<{ dateEnd?: string | null; mr080Amount?: string | number | null; batchAmount?: string | number | null }>,
): string | null {
  let latest: string | null = null;
  for (const batch of batches) {
    if (!batchHasMoney(batch.mr080Amount, batch.batchAmount)) continue;
    const end = asReconDay(batch.dateEnd);
    if (!end) continue;
    if (latest == null || end > latest) latest = end;
  }
  return latest;
}

/** Open set dates: the day after the last paid batch through 2026-12-31. */
export function openReconDays(yearDays: string[], lastClosedEnd: string | null): string[] {
  if (!lastClosedEnd) return yearDays;
  const start = nextReconDay(lastClosedEnd);
  return yearDays.filter((d) => d >= start);
}

export function sumMoneyCells(values: Array<string | number | null | undefined>): string {
  let total = 0;
  let any = false;
  for (const raw of values) {
    const n = parseMoneyCell(raw == null ? '' : String(raw));
    if (n === null) continue;
    total += n;
    any = true;
  }
  return any ? total.toFixed(2) : '';
}
